import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MapIcon from "@mui/icons-material/Map";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import {BreathRibbons} from "../three/BreathRibbons";
import {MindfulHeroIllustration} from "./MindfulHeroIllustration";

export const HeroSection = ({onScrollTo}) => {
    return (
        <Box
            component="section"
            id="hero"
            aria-labelledby="hero-title"
            sx={{
                pt: {xs: 3, md: 5},
                pb: {xs: 4, md: 6},
                width: "100%",
                maxWidth: "100%",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    p: {xs: 2, sm: 4, md: 5},
                    borderRadius: 4,
                    background:
                        "linear-gradient(135deg, rgba(235, 242, 237, 0.7) 0%, rgba(250, 248, 245, 0.95) 100%)",
                    border: "1px solid",
                    borderColor: "rgba(40, 82, 56, 0.12)",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
                    display: "flex",
                    flexDirection: {xs: "column", md: "row"},
                    alignItems: "center",
                    gap: {xs: 3, md: 5},
                }}
            >
                {/* Ethereal 3D Breath Ribbons & Light Spores in the background */}
                <BreathRibbons opacity={0.16} sporeCount={16} />

                {/* Left: Text & CTAs */}
                <Box sx={{flex: 1, minWidth: 0, position: "relative", zIndex: 1}}>
                    <Typography
                        id="hero-title"
                        variant="h3"
                        component="h1"
                        sx={{
                            fontWeight: 800,
                            fontSize: {xs: "1.75rem", sm: "2.2rem", md: "2.6rem"},
                            color: "primary.main",
                            lineHeight: 1.2,
                            letterSpacing: "-0.02em",
                            mb: 2,
                        }}
                    >
                        Få kontroll på bekymringene – finn ro i hverdagen
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: "text.secondary",
                            fontSize: {xs: "1rem", sm: "1.125rem"},
                            lineHeight: 1.7,
                            mb: 3,
                            maxWidth: 620,
                        }}
                    >
                        Et dokumentert og helhetlig mestringskurs utviklet for å hjelpe deg ut av tankefellene og tilbake til et mer tilstede og roligere liv.
                    </Typography>

                    {/* Dual Action CTAs */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: {xs: "column", sm: "row"},
                            gap: 2,
                            mb: 4,
                            width: "100%",
                            maxWidth: "100%",
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<MapIcon />}
                            onClick={() => onScrollTo?.("finn-kursholder")}
                            sx={{
                                minHeight: 48,
                                px: {xs: 2, sm: 3},
                                width: {xs: "100%", sm: "auto"},
                                maxWidth: "100%",
                                borderRadius: 3,
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: "1rem",
                                whiteSpace: {xs: "normal", sm: "nowrap"},
                                boxShadow: "0 4px 12px rgba(40, 82, 56, 0.25)",
                                "&:focus-visible": {
                                    outline: "2px solid #285238",
                                    outlineOffset: 2,
                                },
                            }}
                        >
                            Finn kursholder nær deg
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            size="large"
                            startIcon={<PlayCircleOutlineIcon />}
                            onClick={() => onScrollTo?.("meditasjon")}
                            sx={{
                                minHeight: 48,
                                px: {xs: 2, sm: 3},
                                width: {xs: "100%", sm: "auto"},
                                maxWidth: "100%",
                                borderRadius: 3,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "1rem",
                                whiteSpace: {xs: "normal", sm: "nowrap"},
                                borderColor: "primary.main",
                                "&:hover": {
                                    backgroundColor: "rgba(40, 82, 56, 0.06)",
                                },
                                "&:focus-visible": {
                                    outline: "2px solid #285238",
                                    outlineOffset: 2,
                                },
                            }}
                        >
                            Prøv 8 min meditasjon
                        </Button>
                    </Box>

                    {/* Trust Badges */}
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: {xs: 1.5, sm: 3},
                            pt: 1,
                            borderTop: "1px solid",
                            borderColor: "rgba(40, 82, 56, 0.12)",
                        }}
                    >
                        {[
                            "Dokumentert effekt",
                            "Sertifiserte kursholdere",
                            "Kommunal forankring",
                        ].map((badge) => (
                            <Box
                                key={badge}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                }}
                            >
                                <CheckCircleOutlineIcon
                                    sx={{color: "primary.main", fontSize: "1.1rem"}}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 600,
                                        color: "text.primary",
                                        fontSize: "0.85rem",
                                    }}
                                >
                                    {badge}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Right: Calming Illustration with Mindful 2.5D Living Depth */}
                <Box
                    sx={{
                        flex: {xs: "none", md: "0 0 380px"},
                        width: "100%",
                        maxWidth: {xs: 340, md: 380},
                        display: "flex",
                        justifyContent: "center",
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    <MindfulHeroIllustration
                        src="/meditation.jpg"
                        alt="Illustrasjon av en person som finner ro gjennom oppmerksomt nærvær"
                    />
                </Box>
            </Box>
        </Box>
    );
};
